// "See it in your room", for every entry point: the Shelf's cube button, the
// Label sheet and the Coffee page. Picks the route (src/lib/ar-route.js) and
// opens it:
//
//   native      the iOS app's walkable shop (src/lib/shelf-ar.js)
//   webxr       the walkable shop in the browser (src/components/WebShop.vue, AR)
//   quicklook   iPhone Safari: the bay (Shelf) or the coffee on its board
//               (Label sheet / Coffee page, with the "Add to bag" banner)
//               in AR Quick Look (src/lib/quicklook.js)
//   preview     the walkable shop in the page, without the camera
//   app-viewer  an app build without ShelfAR: Quick Look / Scene Viewer (src/lib/ar.js)
//
//   seeInRoom({ bayKey?, pick?, size? })
//     bayKey  the bay to stand in front of (the Shelf's bay in view)
//     pick    a coffee slug: open with it in hand (the Label sheet, the Coffee page)
//     size    that coffee's chosen size (the Quick Look banner's price)
//
// Light on purpose (pages import it statically): everything heavy is a
// dynamic import behind the route that needs it. The WebXR session is
// requested before anything loads, while the tap still counts as one.

import { isNative } from '../core/native.js';
import { bays, coffeeBySlug } from '../store.js';
import { arRoute, webXRSupported } from './ar-route.js';
import { openWebShop } from './web-shop-state.js';
import { layoutWall } from './placement.js';
import { wallInput } from './wall-look.js';

// Ask early, so the tap does not wait on it.
if (typeof window !== 'undefined') webXRSupported();

const XR_FEATURES = ['hit-test', 'dom-overlay', 'light-estimation', 'local-floor', 'anchors'];

function bayOf ({ bayKey, pick }) {
  const list = bays.value.filter((b) => b.coffees.length);
  return list.find((b) => b.key === bayKey)
    || (pick && list.find((b) => b.coffees.some((c) => c.slug === pick)))
    || list[0];
}

/** The placed items of a bay, or of one coffee on a bay of its own. */
function itemsOf (bay, coffee) {
  if (coffee) {
    const { items } = layoutWall(wallInput([{ key: coffee.slug, title: coffee.name, coffees: [coffee] }]));
    return { items, coffees: [coffee], title: coffee.name };
  }
  const list = bays.value.filter((b) => b.coffees.length);
  const layout = layoutWall(wallInput(list));
  return { items: layout.items.filter((it) => it.bay === bay.key), coffees: bay.coffees, title: `${bay.title} · BeanHub Shelf` };
}

async function startXR (state) {
  const root = document.createElement('div');
  root.className = 'webshop-root';
  document.body.appendChild(root);
  try {
    const session = await navigator.xr.requestSession('immersive-ar', {
      optionalFeatures: XR_FEATURES,
      domOverlay: { root },
    });
    openWebShop({ ...state, mode: 'xr', session, root });
  } catch (e) {
    // No camera permission, or the session could not start: the shop without AR.
    console.warn('[see-in-room] AR session', e);
    root.remove();
    openWebShop({ ...state, mode: 'preview' });
  }
}

export async function seeInRoom ({ bayKey, pick, size } = {}) {
  const bay = bayOf({ bayKey, pick });
  if (!bay) return;
  const coffee = pick ? coffeeBySlug(pick) : null;
  const state = { bayKey: bay.key, pick: pick || null };

  if (isNative) {
    const shelf = await import('./shelf-ar.js');
    if (shelf.canWalkShelf()) { await shelf.openShelfAR({ bayKey: bay.key, pick }); return; }
    const [{ buildBayGroup }, { seeInRoom: systemViewer }] = await Promise.all([import('../three/bayExport.js'), import('./ar.js')]);
    const { items, coffees, title } = itemsOf(bay, coffee);
    const group = buildBayGroup(items, coffees);
    await group.userData.ready;   // every front drawn, or the viewer gets blank bags
    await systemViewer(group, { title, kind: 'bay' });
    return;
  }

  const route = await arRoute();
  if (route === 'webxr') { await startXR(state); return; }
  if (route === 'quicklook') {
    const { openQuickLook } = await import('./quicklook.js');
    const { items, coffees, title } = itemsOf(bay, coffee);
    await openQuickLook({ items, coffees, title, banner: coffee ? { coffee, size } : null });
    return;
  }
  openWebShop({ ...state, mode: 'preview' });
}
